import { Component, OnInit } from "@angular/core";
import { AdminService } from "src/app/shared/services/admin.service";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PayService } from "src/app/shared/services/pay.service";
import { PlaysService } from "src/app/shared/services/plays/plays.service";

interface CreatorApplication {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  socialLinks: any;
}

interface PendingPayment {
  id: string;
  creatorId: string;
  creatorName?: string;
  amount: number;
  requestedDate: any; // Firestore timestamp
  status: string;
}

interface CreatorOption {
  id?: string; // Optional because new options won't have it yet
  name: string;
  price: {
    monthly: number | string;
    yearly: number | string;
  };
  priceIds?: {
    monthly: string;
    yearly: string;
  };
  description: string;
  features: string[];
  cta: string;
  payoutSpeed: string;
  platformFee: string;
  rank: number;
}

@Component({
  selector: "app-admin",
  templateUrl: "./admin.component.html",
  styleUrls: ["./admin.component.scss"],
})
export class AdminComponent implements OnInit {
  currentUser: any;
  isLoading = true;
  view: "applications" | "payments" | "subscriptions" = "applications"; // To toggle views

  // Admin data
  creatorApplications: CreatorApplication[] = [];
  pendingPayments: PendingPayment[] = [];
  creatorOptions: CreatorOption[] = [];

  // State for inline editing of subscription options
  editingCell: { optionId: string; field: string } | null = null;
  editedData: any = null;
  isAddingNewOption = false;
  newOptionData: CreatorOption | null = null;

  // Getter/setter to bridge the features array and the textarea string
  get editedFeaturesAsString(): string {
    if (
      this.editingCell?.field === "features" &&
      Array.isArray(this.editedData)
    ) {
      return this.editedData.join("\n");
    }
    return "";
  }

  set editedFeaturesAsString(value: string) {
    if (this.editingCell?.field === "features") {
      this.editedData = value.split("\n");
    }
  }

  constructor(
    private authService: AuthService,
    private playsService: PlaysService,
    private adminService: AdminService,
    private payService: PayService
  ) {}

  ngOnInit(): void {
    this.authService.ui$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        // Assuming an admin user has a specific role or property like 'isAdmin'
        if (this.currentUser.a4aAdmin) {
          this.loadAdminData();
        } else {
          // If not an admin, stop loading and show an access denied message in the template
          this.isLoading = false;
          console.error("Access Denied: User is not an admin.");
        }
      }
    });
  }

  async loadAdminData(): Promise<void> {
    this.isLoading = true;
    try {
      const [creatorApplications, pendingPayments, creatorOptions] =
        await Promise.all([
          this.playsService.getCreatorApplications(),
          this.playsService.getPendingPayments(),
          this.payService.getCreatorPlans(), // Fetch creator options
        ]);

      this.creatorApplications = creatorApplications;
      this.pendingPayments = pendingPayments;
      // Process and sort creator options
      this.creatorOptions = creatorOptions.map((option) => ({
        ...option,
        price: {
          monthly:
            option.price.monthly === "Free"
              ? 0
              : parseFloat(option.price.monthly),
          yearly:
            option.price.yearly === "Free"
              ? 0
              : parseFloat(option.price.yearly),
        },
        // Ensure features is an array to prevent errors if it's missing
        features: option.features || [],
        // Ensure priceIds object exists to prevent errors if it's missing
        priceIds: option.priceIds || { monthly: "", yearly: "" },
      }));
    } catch (error) {
      console.error("Error loading admin data:", error);
    } finally {
      this.isLoading = false;
    }
  }

  async onApproveApplication(application: CreatorApplication): Promise<void> {
    if (
      confirm(`Are you sure you want to approve ${application.displayName}?`)
    ) {
      try {
        // This service method is responsible for upgrading the user's account (e.g., setting creatorType)
        // and removing the creator application record.
        await this.playsService.approveCreatorApplication(application);
        alert(
          `${application.displayName} has been approved and their account has been upgraded.`
        );
        this.loadAdminData(); // Refresh data
      } catch (error) {
        console.error("Error approving application:", error);
        alert("Failed to approve application.");
      }
    }
  }

  async onDenyApplication(application: CreatorApplication): Promise<void> {
    if (
      confirm(
        `Are you sure you want to deny the application from ${application.displayName}?`
      )
    ) {
      try {
        // This service method is responsible for deleting the creator application record.
        await this.playsService.denyCreatorApplication(application.id);
        alert(
          `Application from ${application.displayName} has been denied and removed.`
        );
        this.loadAdminData(); // Refresh data
      } catch (error) {
        console.error("Error denying application:", error);
        alert("Failed to deny application.");
      }
    }
  }

  async onApprovePayment(payment: PendingPayment): Promise<void> {
    if (
      confirm(
        `Approve payout of ${payment.amount} for ${
          payment.creatorName || payment.creatorId
        }?`
      )
    ) {
      try {
        await this.playsService.processPendingPayment(payment.id, "approved");
        alert("Payment approved.");
        this.loadAdminData(); // Refresh data
      } catch (error) {
        console.error("Error approving payment:", error);
        alert("Failed to approve payment.");
      }
    }
  }

  async onDenyPayment(payment: PendingPayment): Promise<void> {
    if (
      confirm(
        `Deny payout of ${payment.amount} for ${
          payment.creatorName || payment.creatorId
        }?`
      )
    ) {
      try {
        await this.playsService.processPendingPayment(payment.id, "denied");
        alert(
          "Payment denied. Transactions have been returned to the creator's unpaid balance."
        );
        this.loadAdminData(); // Refresh data
      } catch (error) {
        console.error("Error denying payment:", error);
        alert("Failed to deny payment.");
      }
    }
  }

  // --- Subscription Options Methods ---

  startEdit(
    option: CreatorOption,
    field: "name" | "price" | "description" | "features"
  ): void {
    if (this.editingCell || this.isAddingNewOption) {
      alert("Please save or cancel your current changes first.");
      return;
    }
    this.editingCell = { optionId: option.id!, field };

    switch (field) {
      case "price":
        // Deep copy to avoid modifying the original object during edit
        this.editedData = JSON.parse(
          JSON.stringify({
            price: option.price,
            priceIds: option.priceIds,
          })
        );
        break;
      case "features":
        // Keep editedData as an array. The template will use a getter/setter.
        this.editedData = [...option.features]; // Create a shallow copy to edit
        break;
      default: // name, description
        this.editedData = option[field];
        break;
    }
  }

  onAddNewOption(): void {
    // Prevent adding if an option is already being edited.
    if (this.editingCell || this.isAddingNewOption) {
      alert("Please save or cancel your current changes first.");
      return;
    }
    this.isAddingNewOption = true;
    this.newOptionData = {
      // No 'id' property for new options
      name: "New Tier",
      price: { monthly: 0, yearly: 0 },
      priceIds: { monthly: "", yearly: "" },
      description: "",
      features: ["New feature"],
      cta: "Sign Up",
      payoutSpeed: "Standard",
      platformFee: "15%",
      rank: this.creatorOptions.length,
    };
  }

  onCancel(): void {
    this.editingCell = null;
    this.editedData = null;
    this.isAddingNewOption = false;
    this.newOptionData = null;
  }

  async onSave(optionToSave: CreatorOption): Promise<void> {
    if (!this.editingCell) return;

    const field = this.editingCell.field;

    // Apply the changes from editedData to the option object
    switch (field) {
      case "price":
        optionToSave.price = this.editedData.price;
        optionToSave.priceIds = this.editedData.priceIds;
        break;
      case "features":
        // editedData is now an array, so we can process it directly.
        optionToSave.features = (this.editedData as string[])
          .map((f) => f.trim())
          .filter(Boolean);
        break;
      default:
        (optionToSave as any)[field] = this.editedData;
        break;
    }

    this.isLoading = true;
    try {
      await this.adminService.saveCreatorOption(optionToSave);
      alert("Subscription option saved successfully!");
      this.onCancel(); // Reset state
      await this.loadAdminData(); // Reload data to get fresh state from DB
    } catch (error) {
      console.error("Error saving subscription option:", error);
      alert("Failed to save option. See console for details.");
      this.isLoading = false; // Stop loading on error
    }
  }

  async onSaveNewOption(): Promise<void> {
    if (!this.newOptionData) return;
    this.isLoading = true;
    try {
      await this.adminService.saveCreatorOption(this.newOptionData);
      alert("New subscription option added successfully!");
      this.onCancel(); // Reset state
      await this.loadAdminData();
    } catch (error) {
      console.error("Error saving new option:", error);
      alert("Failed to save new option. See console for details.");
      this.isLoading = false;
    }
  }

  async onDeleteOption(option: CreatorOption): Promise<void> {
    if (!option.id) return;

    if (
      !confirm(
        `Are you sure you want to delete the "${option.name}" tier? This cannot be undone.`
      )
    ) {
      return;
    }

    this.isLoading = true;
    try {
      await this.adminService.deleteCreatorOption(option.id);
      alert(`"${option.name}" tier deleted successfully.`);
      await this.loadAdminData();
    } catch (error) {
      console.error("Error deleting option:", error);
      alert("Failed to delete option. See console for details.");
      this.isLoading = false; // Stop loading on error
    }
  }
}
